/* eslint-disable @next/next/no-img-element */
import React, { useRef } from 'react'
import theme from 'stories/utils/theme'
import { Body2, ButtonText, Headline2, Headline3, Headline4, Subtitle1 } from 'stories/elements/Typography/Text'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { ComponentContainer } from 'components/layouts/Layouts.css'
import _useTranslation from 'lib/hooks/_useTranslation'
import { getReferrerScreen } from 'lib/formatting/url'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import NewButton from 'components/elements/NewButton'

import HighestRating from 'assets/about-tutoring/highest-rating.svg'
import Stack from 'components/elements/Stack'
import { AnalyticsEventProPlanSectionImpression } from 'lib/constants/AnalyticsEventType'
import useImpressionTracking from 'lib/hooks/useImpressionTracking'

const Card = styled.div<{ isWhiteColor?: boolean; isScrollable?: boolean }>`
    height: auto;
    min-height: 10px;
    width: 390px;
    position: relative;
    padding: 15px;
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.normal};
    background: linear-gradient(0deg, rgba(10, 10, 10, 0.05), rgba(10, 10, 10, 0.05)),
        linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15));

    position: relative;
    gap: 10px;
    .credentials {
        display: grid;
    }

    @media (max-width: 750px) {
        width: 100%;
        min-height: 100px;
        height: auto;
        ${({ isScrollable }) =>
            isScrollable
                ? css`
                      width: 300px;
                      min-height: 140px;
                  `
                : ''}
    }
`

const Rating = styled(HighestRating)`
    position: relative;
    right: 5px;
`

const Container = styled.div`
    background: linear-gradient(0deg, rgba(10, 10, 10, 0.05), rgba(10, 10, 10, 0.05)),
        linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15));
    padding: 35px 25px;
    border-radius: ${theme.borderRadius.normal};
    width: 100%;

    .reviews {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 15px;
        overflow: hidden;
        width: 100%;
        position: relative;
        @media (max-width: 1050px) {
            flex-wrap: wrap;
        }

        .filter {
            left: 0;

            border-left: 30px solid #55afab;
            border-right: 30px solid #4680d9;
            position: absolute;
            z-index: 2;

            height: 100%;
            width: calc(100% + 10px);
            filter: blur(10px);
            @media (max-width: 1050px) {
                display: none;
            }
        }
    }
    .picture-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        justify-content: center;
        align-items: center;
    }
    img {
        height: 64px;
        width: 64px;
        border-radius: 50%;
        background: linear-gradient(0deg, rgba(10, 10, 10, 0.05), rgba(10, 10, 10, 0.05)),
            linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15));
        border: 2px solid ${theme.colors.white};
    }
`

interface Props {
    onGetPlusPlanButtonClick: (value: string) => void
}

const VariantPlusPlanReviews = ({ onGetPlusPlanButtonClick }: Props) => {
    const { t } = _useTranslation('plusPlan')
    const md = useMedia(deviceSize.md)
    const router = useRouter()
    const referrerScreen = getReferrerScreen(router)

    const bannerRef = useRef<HTMLDivElement>(null)

    useImpressionTracking(bannerRef, AnalyticsEventProPlanSectionImpression, { referrerScreen, value: 'social' })

    const pictures = [
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/EWzXsMXKdUbxaivaCCcU.png',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/gDGzewFPhWQHDorGbMoA.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/SqtPiakQmRoTMoFafeqZ.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/wvoTLdGhtxJQSvOPVkrC.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/eBnyYIrPvKxzaztcnGiH.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/MRPMCmOOPUGpROeJjvAQ.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/WYEsTEOxWYbLqfTAdaVg.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/icMfYJCcFQougWirWBGb.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/bNklYfdlduOclZwZDAnn.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/aBFddhTmlIKyjnjegRVk.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/ausVdxGUhQQYjfPmdzPe.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/CiaLmUrUAzNHmUgDbddP.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/JREECkSydqzrjwyXueYy.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/lirkQcgHMlzjDIgezxGJ.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/aBFddhTmlIKyjnjegRVk.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/xmvcnCJhofPGveIYZEEL.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/heZGHrgKWVRXAMHLqUff.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/zaVxfiXNhccgUwoMJdRG.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/rgXfFQinOTNYfEgoKxBc.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/nXfSsAZhAfhskCaZXvEy.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/RhYplncnGADMIznYVktA.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/eXmdOsOJWdxzlqlNWOQJ.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/FsxOzfmDaSgDHuendniL.jpg',
        'https://content-eu-central-1.knowunity.com/PROFILE-PICTURE/QIWpJyTaRdrtpsTZbVwF.jpg',
    ]

    const reviews = [
        {
            user: 'Rtbhzhn, 09.11.2023',
            text: t('plusPlan/subscriptionReviewText1'),
        },
        {
            user: 'ahmed.mora, 18.09.2023',
            text: t('plusPlan/subscriptionReviewText2'),
        },
        {
            user: '#C00KIE, 11.09.2023',
            text: t('plusPlan/subscriptionReviewText3'),
        },
    ]

    const Headline = md ? Headline3 : Headline2

    return (
        <ComponentContainer color={theme.colors.transparent}>
            <section ref={bannerRef}>
                <Container>
                    <Subtitle1
                        className="fadeIn"
                        color={theme.colors.white}
                        textAlign="center"
                        marginBottom={10}
                        fontWeight={500}
                        fontFamily="var(--font-inter)"
                    >
                        {t('plusPlan/reviewsSubtitle')}
                    </Subtitle1>
                    <Headline className="fadeIn" color={theme.colors.white} textAlign="center" marginBottom={50}>
                        {t('plusPlan/reviewsSubscribersTotalHeadline')}
                    </Headline>
                    <Stack gutter={60}>
                        <div className="picture-grid">
                            {pictures.map((picture, i) => (
                                <img
                                    src={picture}
                                    key={picture.split('/').pop()}
                                    alt={`user profile avatar`}
                                    style={{ position: 'relative', left: i > pictures.length * 0.5 ? -8 : 0 }}
                                />
                            ))}
                        </div>
                        <div className="reviews">
                            <div className="filter" />
                            {reviews.map((review, i) => (
                                <Card key={`review-card-${i}`}>
                                    <div className="credentials">
                                        <Rating />
                                        <Body2 color={theme.colors.white} fontWeight={400} fontFamily="var(--font-inter)">
                                            {review.user}
                                        </Body2>
                                    </div>
                                    <Headline4
                                        fontFamily="var(--font-inter)"
                                        fontSize={16}
                                        color={theme.colors.white}
                                        fontWeight={400}
                                        marginBottom={10}
                                    >
                                        {review.text}
                                    </Headline4>
                                </Card>
                            ))}
                        </div>
                        <NewButton
                            borderRadius={theme.borderRadius.normal}
                            color={theme.colors.white}
                            width={md ? undefined : 450}
                            fullWidth={md}
                            onClick={() => onGetPlusPlanButtonClick('social')}
                        >
                            <ButtonText color={theme.colors.businessBlue}>{t('plusPlan/joinProCommunityCTA')}</ButtonText>
                        </NewButton>
                    </Stack>
                </Container>
            </section>
        </ComponentContainer>
    )
}

export default VariantPlusPlanReviews
